import React, { useCallback, useMemo } from "react";
import { ItemCategory, CategorySelection } from "@/lib/types";
import type { CategoryStructure } from "@/lib/categoryStructure";
import SearchableSelect, {
  SearchableSelectOption,
} from "@/components/ui/SearchableSelect";

interface MarketCategoryDropdownProps {
  selectedCategory: CategorySelection | null;
  onCategorySelect: (category: CategorySelection | null) => void;
  categoryStructure: CategoryStructure[];
}

const MarketCategoryDropdown = React.memo(
  ({
    selectedCategory,
    onCategorySelect,
    categoryStructure,
  }: MarketCategoryDropdownProps) => (
    <MarketCategoryDropdownInner
      selectedCategory={selectedCategory}
      onCategorySelect={onCategorySelect}
      categoryStructure={categoryStructure}
    />
  ),
);
MarketCategoryDropdown.displayName = "MarketCategoryDropdown";

function MarketCategoryDropdownInner({
  selectedCategory,
  onCategorySelect,
  categoryStructure,
}: MarketCategoryDropdownProps) {
  const options = useMemo<SearchableSelectOption[]>(() => {
    const opts: SearchableSelectOption[] = [
      { value: "", label: "All Categories", group: "General" },
    ];

    for (const category of categoryStructure) {
      // Category-level selection (subcategoryId is empty)
      opts.push({
        value: `cat:${category.id}`,
        label: category.name,
        group: category.name,
      });

      for (const subcategory of category.subcategories) {
        opts.push({
          value: `sub:${category.id}:${subcategory.id}`,
          label: subcategory.name,
          group: category.name,
        });
      }
    }

    return opts;
  }, [categoryStructure]);

  const selectedValue = useMemo(() => {
    if (!selectedCategory) return "";
    if (!selectedCategory.subcategoryId) {
      return `cat:${selectedCategory.categoryId}`;
    }
    return `sub:${selectedCategory.categoryId}:${selectedCategory.subcategoryId}`;
  }, [selectedCategory]);

  const handleChange = useCallback(
    (nextValue: string) => {
      if (!nextValue) {
        onCategorySelect(null);
        return;
      }

      const [kind, categoryIdRaw, subcategoryIdRaw] = nextValue.split(":");
      if (!kind || !categoryIdRaw) {
        onCategorySelect(null);
        return;
      }

      const categoryId = categoryIdRaw as ItemCategory;
      const category = categoryStructure.find((c) => c.id === categoryId);
      if (!category) {
        onCategorySelect(null);
        return;
      }

      if (kind === "cat") {
        onCategorySelect({
          categoryId: category.id,
          subcategoryId: "",
          categoryName: category.name,
          subcategoryName: "",
        });
        return;
      }

      if (kind === "sub" && subcategoryIdRaw) {
        const subcategory = category.subcategories.find(
          (s) => s.id === subcategoryIdRaw,
        );

        onCategorySelect({
          categoryId: category.id,
          subcategoryId: subcategoryIdRaw,
          categoryName: category.name,
          subcategoryName: subcategory?.name ?? "",
        });
      } else {
        onCategorySelect(null);
      }
    },
    [categoryStructure, onCategorySelect],
  );

  return (
    <SearchableSelect
      options={options}
      value={selectedValue}
      onChange={handleChange}
      placeholder="All Categories"
      className="h-10"
    />
  );
}

export default MarketCategoryDropdown;
